import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@/fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

interface PieChartData {
  durchgegangen: number
  eingebucht: number
  knapp: number
}

interface StatCardWithPieChartProps {
  title: string
  icon: IconDefinition
  data: PieChartData
  loading: boolean
  onClick: () => void
  dataTour?: string
}

const COLORS = {
  durchgegangen: '#10b981', // green-500
  eingebucht: '#ef4444',     // red-500
  knapp: '#f97316'           // orange-500
}

const DARK_COLORS = {
  durchgegangen: '#22c55e', // green-400
  eingebucht: '#f87171',     // red-400
  knapp: '#fb923c'           // orange-400
}

export default function StatCardWithPieChart({
  title,
  icon,
  data,
  loading,
  onClick,
  dataTour
}: StatCardWithPieChartProps) {
  // Validate data prop
  if (!data || typeof data !== 'object') {
    data = { durchgegangen: 0, eingebucht: 0, knapp: 0 }
  }

  // Extract values for rendering
  const durchgegangen = data.durchgegangen;
  const eingebucht = data.eingebucht;
  const knapp = data.knapp;

  const total = durchgegangen + eingebucht + knapp

  // Transform data for recharts - use validated number values
  const chartData = [
    { name: 'Geht durch', value: durchgegangen, color: 'durchgegangen' },
    { name: 'Wird eingebucht', value: eingebucht, color: 'eingebucht' },
    { name: 'Wird knapp', value: knapp, color: 'knapp' }
  ].filter(item => item.value > 0) // Only show segments with data

  interface TooltipProps {
    active?: boolean
    payload?: Array<{
      name: string
      value: number
      color: string
    }>
  }

  const CustomTooltip = ({ active, payload }: TooltipProps) => {
    if (active && payload && payload.length > 0) {
      const item = payload[0]
      const percentage = ((item.value / total) * 100).toFixed(1)
      return (
        <div className="bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {item.name}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {item.value} ({percentage}%)
          </p>
        </div>
      )
    }
    return null
  }

  if (loading) {
    return (
      <div data-tour={dataTour} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700 min-h-80 overflow-hidden flex items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (total === 0) {
    return (
      <div
        data-tour={dataTour}
        onClick={onClick}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700 min-h-80 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
      >
        <div className="flex items-center space-x-2 mb-4">
          <div className="shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
            <FontAwesomeIcon icon={icon} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate min-w-0">{title}</h3>
        </div>
        <div className="flex flex-col items-center justify-center h-[180px]">
          <p className="text-gray-400 dark:text-gray-600 text-sm">Keine Daten</p>
        </div>
      </div>
    )
  }

  return (
    <div
      data-tour={dataTour}
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700 min-h-80 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col"
    >
      {/* Top section - grows to push legend down */}
      <div className="flex-1">
        <div className="flex items-center space-x-2 mb-4">
          <div className="shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
            <FontAwesomeIcon icon={icon} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate min-w-0">{title}</h3>
        </div>

        {/* PieChart */}
        <div className="h-[148px]">
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={55}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => {
                  const isDark = document.documentElement.classList.contains('dark')
                  const colorMap = isDark ? DARK_COLORS : COLORS
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={colorMap[entry.color as keyof typeof COLORS]}
                    />
                  )
                })}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Legend - use validated number values */}
      <div className="flex flex-col space-y-2 pt-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-green-500 dark:bg-green-400"></div>
            <span className="text-gray-600 dark:text-gray-400">Geht durch</span>
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{durchgegangen}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500 dark:bg-red-400"></div>
            <span className="text-gray-600 dark:text-gray-400">Wird eingebucht</span>
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{eingebucht}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-orange-500 dark:bg-orange-400"></div>
            <span className="text-gray-600 dark:text-gray-400">Wird knapp</span>
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{knapp}</span>
        </div>
      </div>
    </div>
  )
}
